import React, {
    useState, useCallback, forwardRef
} from 'react';
import PropTypes from 'prop-types';

import { Tracking } from 'bdvp-tracking';

import Carousel from '../Carousel';
import RowHeading from '../RowHeading';
import StoryCard from '../StoryCard';
import Modal from './Modal';

import { CarouselRowType } from '../../utils/constants';

import {
    imgSizes, PARTIAL_VISIBILITY_GUTTER, LAYOUT_ITEMS
} from './constants';

const carouselType = CarouselRowType.STORIES;

const StoriesRow = forwardRef(({
    title,
    list,
    hasMore,
    fetchMoreData,
    isLoading,
    isLoadingMore,
    limit,
    rank,
    playlist,
    ...otherProps
}, ref) => {

    const [
        currentStoryUrl,
        setCurrentStoryUrl,
    ] = useState('');

    const onAfterChangeHandler = useCallback((isScrollLeft) => {

        Tracking.trackCarouselScroll({
            carouselRank: rank,
            carouselTitle: title,
            isScrollLeft,
            carouselType: carouselType,
        });

    }, [
        rank,
        title,
    ]);

    const onItemClickHandler = useCallback((event, item, index) => {

        // event.preventDefault() is required so that the user isn't navigated to the hyperlink
        event.preventDefault();
        setCurrentStoryUrl(item.href);

        Tracking.trackCarouselOnItemClick({
            carouselRank: rank,
            carouselTitle: title,
            index,
            id: item.title,
            carouselType: carouselType,
        });

    }, [
        rank,
        title,
        setCurrentStoryUrl,
    ]);

    const onModalCloseHandler = useCallback(() => {

        setCurrentStoryUrl('');

    }, [setCurrentStoryUrl]);

    const renderItem = useCallback((item, isLoading, index = 0) => (
        <StoryCard
            as={'a'}
            href={item.href}
            onClick={(event) => onItemClickHandler(event, item, index)}
            imgSizes={imgSizes}
            title={item.title}
            imageTemplateUrl={item.imageTemplateUrl}
            isNew={item.isNew}
            loading={isLoading}
        />
    ), [onItemClickHandler]);

    if (list.length === 0 && !isLoading) {

        return null;

    }

    return (
        <div ref={ref} {...otherProps}>
            <RowHeading
                loading={isLoading}
                text={title}
                rank={rank}
                enableLeftRightMargin
            />
            <Carousel
                hasMore={hasMore}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                limit={limit}
                list={list}
                fetchMoreData={fetchMoreData}
                renderItem={renderItem}
                onAfterChange={onAfterChangeHandler}
                partialVisibilityGutter={PARTIAL_VISIBILITY_GUTTER}
                layoutItems={LAYOUT_ITEMS}
            />
            <Modal
                stories={list}
                currentStoryUrl={currentStoryUrl}
                onClose={onModalCloseHandler}
                playlist={playlist}
            />
        </div>
    );

});

StoriesRow.propTypes = {
    fetchMoreData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    limit: PropTypes.number.isRequired,
    rank: PropTypes.number.isRequired,
    hasMore: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    list: PropTypes.array.isRequired,
    playlist: PropTypes.string.isRequired,
};

export default StoriesRow;
